import React, { useState, useEffect, useRef } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { 
  User, 
  Brain, 
  LogOut, 
  Check, 
  X,
  Pencil,
  Calendar,
  Download
} from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import { getPreferences, updatePreferences } from '../../utils/memoryApi';

/**
 * Date format options with examples
 */
const DATE_FORMATS = [
  { value: 'DMY', label: '2 December 2025', description: 'Day Month Year' },
  { value: 'MDY', label: 'December 2, 2025', description: 'Month Day Year' },
  { value: 'ISO', label: '2025-12-02', description: 'Year-Month-Day' }
];

/**
 * ProfileMenu - Dropdown menu for account settings
 * 
 * Contains:
 * - Display name (editable)
 * - Date format preference
 * - Your memories link (placeholder for now)
 * - Sign out
 */
export default function ProfileMenu({ 
  user, 
  onClose, 
  onDisplayNameChange,
  dateFormat = 'DMY',
  onDateFormatChange
}) {
  const { logout, getToken } = useKindeAuth();
  const menuRef = useRef(null);
  
  // Display name state
  const [displayName, setDisplayName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Date format state
  const [localDateFormat, setLocalDateFormat] = useState(dateFormat);
  const [isSavingFormat, setIsSavingFormat] = useState(false);
  
  // PWA install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Get display name from Kinde as fallback
  const kindeDisplayName = user?.given_name || user?.email?.split('@')[0] || 'User';

  // Load preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      try {
        const token = await getToken();
        if (token) {
          const result = await getPreferences(token);
          if (result.success) {
            if (result.preferences.display_name) {
              setDisplayName(result.preferences.display_name);
            }
            if (result.preferences.date_format) {
              setLocalDateFormat(result.preferences.date_format);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load preferences:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadPreferences();
  }, [getToken]);

  // Check if already installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') {
        if (isEditing) {
          setIsEditing(false);
          setEditValue('');
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isEditing]);

  const handleStartEdit = () => {
    setEditValue(displayName || kindeDisplayName);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSaveDisplayName = async () => {
    if (!editValue.trim()) return;
    
    setIsSaving(true);
    try {
      const token = await getToken();
      if (token) {
        await updatePreferences({ display_name: editValue.trim() }, token);
        const newName = editValue.trim();
        setDisplayName(newName);
        setIsEditing(false);
        setEditValue('');
        // Update parent so greeting updates immediately
        if (onDisplayNameChange) {
          onDisplayNameChange(newName);
        }
      }
    } catch (err) {
      console.error('Failed to save display name:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDateFormatChange = async (newFormat) => {
    setIsSavingFormat(true);
    setLocalDateFormat(newFormat);
    
    try {
      const token = await getToken();
      if (token) {
        await updatePreferences({ date_format: newFormat }, token);
        // Update parent so header updates immediately
        if (onDateFormatChange) {
          onDateFormatChange(newFormat);
        }
      }
    } catch (err) {
      console.error('Failed to save date format:', err);
      // Revert on error
      setLocalDateFormat(dateFormat);
    } finally {
      setIsSavingFormat(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveDisplayName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleSignOut = () => {
    logout();
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // Fallback for browsers that don't support beforeinstallprompt
      alert(
        'To install Mabel:\n\n' +
        'Desktop: Look for the install icon in your browser address bar\n\n' +
        'Android: Tap the menu (⋮) and select "Install app"\n\n' +
        'iOS: Tap Share (↑) and select "Add to Home Screen"'
      );
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User installed Mabel');
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  const currentDisplayName = displayName || kindeDisplayName;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        background: ABLE_COLORS.neutral,
        border: `1px solid ${ABLE_COLORS.border}`,
        borderRadius: ABLE_COLORS.cardRadius,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        minWidth: '260px',
        zIndex: 1000,
        overflow: 'hidden'
      }}
    >
      {/* Header with avatar and name */}
      <div style={{
        padding: '16px',
        borderBottom: `1px solid ${ABLE_COLORS.border}`,
        background: ABLE_COLORS.bgSubtle
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Avatar */}
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: `${ABLE_COLORS.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <User size={20} color={ABLE_COLORS.primary} />
          </div>
          
          {/* Name section */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    border: `1px solid ${ABLE_COLORS.primary}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '600',
                    outline: 'none',
                    minWidth: 0
                  }}
                  maxLength={100}
                />
                <button
                  onClick={handleSaveDisplayName}
                  disabled={isSaving || !editValue.trim()}
                  style={{
                    padding: '4px',
                    background: ABLE_COLORS.primary,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSaving ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isSaving || !editValue.trim() ? 0.5 : 1
                  }}
                  title="Save"
                >
                  <Check size={14} color="white" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  style={{
                    padding: '4px',
                    background: ABLE_COLORS.bgSubtle,
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Cancel"
                >
                  <X size={14} color={ABLE_COLORS.textMuted} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ABLE_COLORS.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {isLoading ? '...' : currentDisplayName}
                </span>
                <button
                  onClick={handleStartEdit}
                  style={{
                    padding: '2px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.6
                  }}
                  title="Edit display name"
                >
                  <Pencil size={12} color={ABLE_COLORS.textMuted} />
                </button>
              </div>
            )}
            <span style={{
              fontSize: '12px',
              color: ABLE_COLORS.textMuted
            }}>
              {user?.email}
            </span>
          </div>
        </div>
      </div>

      {/* Settings section */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${ABLE_COLORS.border}` }}>
        {/* Date Format Setting */}
        <div style={{ marginBottom: '4px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <Calendar size={16} color={ABLE_COLORS.textSecondary} />
            <span style={{
              fontSize: '13px',
              fontWeight: '500',
              color: ABLE_COLORS.textSecondary
            }}>
              Date format
            </span>
          </div>
          <select
            value={localDateFormat}
            onChange={(e) => handleDateFormatChange(e.target.value)}
            disabled={isSavingFormat}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '13px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: '6px',
              background: ABLE_COLORS.neutral,
              color: ABLE_COLORS.textPrimary,
              cursor: isSavingFormat ? 'wait' : 'pointer',
              outline: 'none'
            }}
          >
            {DATE_FORMATS.map(format => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Menu items */}
      <div style={{ padding: '8px 0' }}>
        {/* Install App - only show if not installed */}
        {!isInstalled && (
          <button
            onClick={handleInstall}
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.bgSubtle}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Download size={18} color={ABLE_COLORS.primary} />
            <div>
              <span style={{
                fontSize: '14px',
                color: ABLE_COLORS.textPrimary
              }}>
                Install App
              </span>
              <span style={{
                fontSize: '11px',
                color: ABLE_COLORS.textMuted,
                display: 'block'
              }}>
                Add to home screen
              </span>
            </div>
          </button>
        )}

        {/* Your Memories */}
        <button
          onClick={() => {
            // TODO: Navigate to memories view
            console.log('Memories clicked');
          }}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.bgSubtle}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <Brain size={18} color={ABLE_COLORS.textSecondary} />
          <div>
            <span style={{
              fontSize: '14px',
              color: ABLE_COLORS.textPrimary
            }}>
              Your Memories
            </span>
            <span style={{
              fontSize: '11px',
              color: ABLE_COLORS.textMuted,
              display: 'block'
            }}>
              Coming soon
            </span>
          </div>
        </button>

        {/* Divider */}
        <div style={{
          height: '1px',
          background: ABLE_COLORS.border,
          margin: '8px 0'
        }} />

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.bgSubtle}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <LogOut size={18} color={ABLE_COLORS.semantic?.error || '#EF4444'} />
          <span style={{
            fontSize: '14px',
            color: ABLE_COLORS.semantic?.error || '#EF4444'
          }}>
            Sign Out
          </span>
        </button>
      </div>
    </div>
  );
}
